import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core'
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { LookupService } from '../services/lookup/lookup.service';
import { CombinedUsage, ContractTerm, Customer, HasId, Lookup, PagedResults, SubscriptionDetails, SubscriptionWarning } from '../app.types';
import { BehaviorSubject, combineLatest, Observable, OperatorFunction, Subject } from 'rxjs';
import { filter, map, shareReplay, switchMap, takeUntil, tap } from 'rxjs/operators';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { APP_DATE_FORMATS, AppDateAdapter } from 'src/util/app-date-adapter';
import { ContractTermService } from '../services/contract-term/contract-term.service';
import { convert, round } from 'src/util/conversion-helpers';
import { getUsageThreshold } from 'src/util/threshold-helpers';
import { createNumericalCompare } from 'src/util/array-helpers';
import { nullCheckWrap } from 'src/util/function-helper';
import { PermissionsService } from '../services/permissions/permissions.service';

const MAX_TRIAL_DAYS = 14;
const MAX_START_LEAD_DAYS = 1;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Keeps the redaction trial window sane: a start is required whenever an expiry is set, the window must
 * run forwards, and staff cannot extend it past MAX_TRIAL_DAYS. Mirrors the server's validation, which
 * is what actually enforces it.
 */
function redactionTrialWindowValidator(group: AbstractControl): ValidationErrors {
  const startedAtControl = group.get("redactionTrialStartedAt");
  const expiresAtControl = group.get("redactionTrialExpiresAt");

  // Nothing to police while the dates are read-only. The values came from the server and the user cannot
  // change them, so a window stored before the limit existed must not block an unrelated customer save.
  if (!startedAtControl || !expiresAtControl || startedAtControl.disabled || expiresAtControl.disabled) {
    return null;
  }

  const startedAt = startedAtControl.value;
  const expiresAt = expiresAtControl.value;

  if (!startedAt && !expiresAt) {
    return null;
  }

  if (!startedAt) {
    return { redactionTrialStartMissing: true };
  }

  if (!expiresAt) {
    return { redactionTrialExpiryMissing: true };
  }

  // The gate only knows when a trial ends, so a window post-dated to begin later would hand out redaction
  // from the moment it was saved. A day of lead is allowed for dates picked either side of UTC midnight.
  if (new Date(startedAt).getTime() > Date.now() + MAX_START_LEAD_DAYS * MS_PER_DAY) {
    return { redactionTrialStartInFuture: true };
  }

  const days = (new Date(expiresAt).getTime() - new Date(startedAt).getTime()) / MS_PER_DAY;

  if (days < 0) {
    return { redactionTrialWindowReversed: true };
  }

  return days > MAX_TRIAL_DAYS ? { redactionTrialTooLong: true } : null;
}

@Component({
    selector: 'customer-form',
    templateUrl: './customer-form.component.html',
    styleUrls: ['./customer-form.component.scss'],
    providers: [
        { provide: DateAdapter, useClass: AppDateAdapter },
        { provide: MAT_DATE_FORMATS, useValue: APP_DATE_FORMATS }
    ],
    standalone: false
})
export class CustomerFormComponent implements OnInit, OnDestroy {
  @Input() form: FormGroup;
  @Input() customerSubject = new Subject<Customer>();
  dateLowerBound = new Date();
  dateUpperBound = new Date();

  _isFormLoading = false;

  @Input()
  public set isFormLoading(v : boolean) {
    // Recorded before the branch below, which reads it back through applyTrialWindowAccess(). Assigning
    // afterwards left that call seeing the form as still loading on the one transition that hands the
    // trial dates over, so they were re-disabled the moment the customer finished loading.
    this._isFormLoading = v;

    if(v){
      this.form.disable();
    } else {
      this.form.enable();
      if(!this.form.get("subscriptionDetails.cloudServicesEnabled")?.value) {
        this.form.get("subscriptionDetails.streamVideoEnabled")?.disable();
      }
      this.applyTrialWindowAccess();
    }
  }
  
  public get isFormLoading() : boolean {
    return this._isFormLoading;
  }
  
  
  usageDetails?: Record<keyof CombinedUsage, { amount: number, warningLevel: SubscriptionWarning}> = null;

  _usage: CombinedUsage;

  @Input()
  public set usage(v : CombinedUsage) {
    this._usage = v;
    if(!this.form.get("subscriptionDetails")) {return;}
    
    this.updateUsageDetails(this.form.get("subscriptionDetails").getRawValue());
  }
  
  errorMessageLists = {
    name: {
      required: "*Please enter a customer name"
    },
    customerInstanceUrl: {
      required: "*Please enter a customer instance URL"
    },
    tenantId: {
      required: "*Please enter a Tenant ID",
      pattern: "*Please enter a GUID"
    },
    contact: {
      pattern: "*Please enter a valid email address"
    },
    integratorContact: {
      pattern: "*Please enter a valid email address"
    }
  }

  assetCount: number;
  assetCountWarning: SubscriptionWarning;

  customerTypes: Lookup[];
  addCustomerType = this.createAddLookUpFunc("customerType");
  editCustomerType= this.createEditLookUpFunc("customerType"); 
  deleteCustomerType = this.createDeleteLookUpFunc("customerType"); 
  customerTypeRefresh$ = new BehaviorSubject<void>(undefined);

  customerInstanceUrls: Lookup[];
  addCustomerInstanceUrl = this.createAddLookUpFunc("customerInstanceUrl");
  editCustomerInstanceUrl = this.createEditLookUpFunc("customerInstanceUrl");
  deleteCustomerInstanceUrl = this.createDeleteLookUpFunc("customerInstanceUrl");
  customerInstanceUrlRefresh$ = new BehaviorSubject<void>(undefined);

  contractTerms: Lookup[];

  addContractTerm = (value: string) => {
    const valueInMonths = convert("years", "months", +value);
    return this.contractTermService.addNew({valueInMonths})
      .pipe(this.createHttpResponseMap<ContractTerm>())
  };

  editContractTerm =  (value: Lookup) => {
    const contractTerm = {
      id: value.id, 
      valueInMonths: convert("years", "months", +value.name)
    };

    return this.contractTermService.edit(contractTerm)
      .pipe(this.createHttpResponseMap<ContractTerm>())
  };

  deleteContractTerm =  (contractTerm: ContractTerm) => {
    return this.contractTermService.delete(contractTerm)
      .pipe(this.createHttpResponseMap<ContractTerm>())
  };

  contractTermDisplayNameMapper = (name: string) => name === "0.5" ? "Limited Trial": `${name} years`;
  contractTermRegexValidations = [
    {regex: /^(([1-9])|(1\d)|(20))$/, message: "Term must be a valid number and cannot exceed 20."}
  ];

  contractTermRefresh$ = new BehaviorSubject<void>(undefined);
  
  unSubscribe$ = new Subject();

  /**
   * Whether the redaction trial dates may be changed: only by a Synectics-internal user, and only while
   * the customer is not already entitled to redaction outright, since a trial adds nothing on top of a
   * subscription. The server independently enforces who may move the window.
   */
  get canEditTrialWindow() {
    return this.permissionsService.isSynxInternal
      && !this.form.get("subscriptionDetails.redactionEnabled")?.value;
  }

  /**
   * Whether there is a window to clear. Offered separately from editing the dates because clearing is
   * what hands the customer their own trial back, and it is the one edit that cannot be typed in a
   * single step: half a window is never valid to save, so both dates have to go together.
   */
  get canClearTrialWindow() {
    return this.canEditTrialWindow
      && !this._isFormLoading
      && (!!this.form.get("subscriptionDetails.redactionTrialStartedAt")?.value
        || !!this.form.get("subscriptionDetails.redactionTrialExpiresAt")?.value);
  }

  get trialWindowError(): string {
    const errors = this.form.get("subscriptionDetails")?.errors;

    if (errors?.redactionTrialStartMissing) { return "*A trial expiry needs a start date."; }
    if (errors?.redactionTrialExpiryMissing) { return "*A trial start needs an expiry date."; }
    if (errors?.redactionTrialStartInFuture) { return "*A trial cannot be set to start in the future."; }
    if (errors?.redactionTrialWindowReversed) { return "*The trial start must not be after its expiry."; }
    if (errors?.redactionTrialTooLong) { return `*A trial must not run longer than ${MAX_TRIAL_DAYS} days.`; }

    return null;
  }

  constructor(private readonly lookupService: LookupService,
    private readonly contractTermService: ContractTermService,
    private readonly fb: FormBuilder,
    private readonly permissionsService: PermissionsService
  ) {
      this.dateLowerBound.setFullYear(this.dateLowerBound.getFullYear() - 10);
      this.dateUpperBound.setFullYear(this.dateUpperBound.getFullYear() + 10);
    }
  
  /**
   * Leaves the trial dates read-only unless the user may edit them. They stay in the payload because the
   * form is submitted with getRawValue(), so a save still round-trips them untouched.
   */
  private applyTrialWindowAccess(): void {
    const startedAtControl = this.form.get("subscriptionDetails.redactionTrialStartedAt");
    const expiresAtControl = this.form.get("subscriptionDetails.redactionTrialExpiresAt");

    if (!startedAtControl || !expiresAtControl) {
      return;
    }

    // emitEvent: false, or toggling these would feed back through the redactionEnabled subscription.
    const options = { emitEvent: false };

    if (this.canEditTrialWindow && !this._isFormLoading) {
      startedAtControl.enable(options);
      expiresAtControl.enable(options);
      return;
    }

    startedAtControl.disable(options);
    expiresAtControl.disable(options);
  }

  /**
   * Takes the whole window away, which is what lets the customer start their own trial again. Marked dirty
   * by hand: setValue() leaves the form pristine, and Save Changes is held closed while it is.
   */
  clearTrialWindow(): void {
    if (!this.canClearTrialWindow) {
      return;
    }

    for (const name of ["redactionTrialStartedAt", "redactionTrialExpiresAt"]) {
      const control = this.form.get(`subscriptionDetails.${name}`);
      control?.setValue(null);
      control?.markAsDirty();
    }
  }

  ngOnDestroy(): void {
    this.unSubscribe$.next(null);
    this.unSubscribe$.complete();
  }

  ngOnInit(): void {
      this.form.addControl("name", new FormControl(null, Validators.required));
      this.form.addControl("enabled", new FormControl(true));
      this.form.addControl("customerInstanceUrlId", new FormControl(null, Validators.required));
      this.form.addControl("tenantId", new FormControl(null, [
        Validators.required, Validators.pattern(/^(\{{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\}{0,1})$/)
      ]));
      this.form.addControl("customerTypeId", new FormControl(null));
      this.form.addControl("createdDate", new FormControl(new Date()));
      this.form.addControl("subscriptionStartDate", new FormControl(null));
      this.form.addControl("contractTerm", new FormControl(""));
      this.form.addControl("subscriptionEndDate", new FormControl({value: null, disabled: true}));
      this.form.addControl("poNumber", new FormControl(null));
      this.form.addControl("contact", new FormControl(null, 
        Validators.pattern(
          /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{1,}))$/
        )
      ));
      this.form.addControl("integratorContact", new FormControl(null, 
        Validators.pattern(
          /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{1,}))$/
        )
      ));
      this.form.addControl("integratorName", new FormControl(null));
      this.form.addControl("subscriptionDetails", this.fb.group({
        assetLimit: new FormControl(null, Validators.min(0)),
        storageLimit: new FormControl(null, Validators.min(0)),
        concurrentUsersLimit: new FormControl(null, Validators.min(0)),
        incidentCreationLimit: new FormControl(null, Validators.min(0)),
        liveVideoLimit: new FormControl(null, Validators.min(0)),
        redactionLimit: new FormControl(null, Validators.min(0)),
        xbaTranscodeLimit: new FormControl(null, Validators.min(0)),
        redactionEnabled: new FormControl(false),
        cloudServicesEnabled: new FormControl(true),
        streamVideoEnabled: new FormControl(true),
        redactionTrialStartedAt: new FormControl(null),
        redactionTrialExpiresAt: new FormControl(null)
      }, { validators: redactionTrialWindowValidator }));

      this.applyTrialWindowAccess();
      

    const streamVideoControl = this.form.get("subscriptionDetails.streamVideoEnabled");
    
    this.form.get("subscriptionDetails.redactionEnabled").valueChanges
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe(() => this.applyTrialWindowAccess());

    this.form.get("subscriptionDetails.cloudServicesEnabled").valueChanges
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe(cloudEnabled => {
        cloudEnabled ? streamVideoControl.enable() : streamVideoControl.disable();
      });
      
      if(this._isFormLoading) {this.form.disable();}

    this.customerSubject.subscribe(customer => {
      const { subscriptionDetails } = customer;
      this.form.get("name").setValue(customer?.name);
      this.form.get("enabled").setValue(customer?.enabled);
      this.form.get("customerInstanceUrlId").setValue(customer?.customerInstanceUrlId);
      this.form.get("tenantId").setValue(customer?.tenantId);
      this.form.get("customerTypeId").setValue(customer?.customerTypeId);
      this.form.get("createdDate").setValue(customer?.createdDate);
      this.form.get("subscriptionStartDate").setValue(customer?.subscriptionStartDate);
      this.form.get("subscriptionEndDate").setValue(customer?.subscriptionEndDate);
      this.form.get("poNumber").setValue(customer?.poNumber);
      this.form.get("contact").setValue(customer?.contact);
      this.form.get("integratorName").setValue(customer?.integratorName);
      this.form.get("integratorContact").setValue(customer?.integratorContact);

      const subDetailsGroup = this.form.get("subscriptionDetails");

      subDetailsGroup.get("assetLimit").setValue(subscriptionDetails?.assetLimit);
      subDetailsGroup.get("storageLimit").setValue(subscriptionDetails?.storageLimit);
      subDetailsGroup.get("concurrentUsersLimit").setValue(subscriptionDetails?.concurrentUsersLimit);
      subDetailsGroup.get("incidentCreationLimit").setValue(subscriptionDetails?.incidentCreationLimit);
      subDetailsGroup.get("liveVideoLimit").setValue(subscriptionDetails?.liveVideoLimit);
      subDetailsGroup.get("redactionLimit").setValue(subscriptionDetails?.redactionLimit);
      subDetailsGroup.get("xbaTranscodeLimit").setValue(subscriptionDetails?.xbaTranscodeLimit);
      subDetailsGroup.get("redactionEnabled").setValue(subscriptionDetails?.redactionEnabled ?? false);
      subDetailsGroup.get("cloudServicesEnabled").setValue(subscriptionDetails?.cloudServicesEnabled ?? true);
      subDetailsGroup.get("streamVideoEnabled").setValue(subscriptionDetails?.streamVideoEnabled ?? true);
      subDetailsGroup.get("redactionTrialStartedAt").setValue(subscriptionDetails?.redactionTrialStartedAt ?? null);
      subDetailsGroup.get("redactionTrialExpiresAt").setValue(subscriptionDetails?.redactionTrialExpiresAt ?? null);

      this.assetCount = customer?.assetCount;
      if(subscriptionDetails?.assetLimit) {this.assetCountWarning = getUsageThreshold(subscriptionDetails.assetLimit, this.assetCount);}
    })

    this.createRefreshLookupObs("customerType", this.customerTypeRefresh$)
      .subscribe(res => {
        if(res.results){
         this.customerTypes = res.results;
        }
      }
    );
    
    this.createRefreshLookupObs("customerInstanceUrl", this.customerInstanceUrlRefresh$)
      .subscribe(res => {
        if(res.results){
          this.customerInstanceUrls = res.results;
        }
      }
    );

    this.contractTermRefresh$.pipe(
      switchMap(() => this.contractTermService.getList({})),
      shareReplay(1),
      takeUntil(this.unSubscribe$)
    ).subscribe(res => {
      if(res.results){
        this.contractTerms = res.results.sort(
          createNumericalCompare(x => x.valueInMonths)
        ).map(x => {
          return {
            id: x.id,
            name: `${convert("months", "years", x.valueInMonths, 1)}`
        }});
      }
      const { subscriptionStartDate, contractTerm, subscriptionEndDate } = this.form.getRawValue();

      if (subscriptionStartDate && contractTerm){
        this.calculateEndDate(
          this.form.get("subscriptionStartDate").value, this.form.get("contractTerm").value);
      }

      if (subscriptionEndDate && subscriptionStartDate && !contractTerm){
        const { startAsDate, endAsDate } = { startAsDate: new Date(subscriptionStartDate), endAsDate: new Date(subscriptionEndDate) };
        const diffInMonths = (endAsDate.getFullYear() - startAsDate.getFullYear()) * 12 + (endAsDate.getMonth() - startAsDate.getMonth());

        const contractTermId = res.results.find(x => x.valueInMonths === diffInMonths)?.id;

        if (contractTermId) {
          this.form.get("contractTerm").setValue(contractTermId);
        }
      }

    });

    combineLatest([
      this.form.get("subscriptionStartDate")?.valueChanges,
      this.form.get("contractTerm")?.valueChanges
    ]).pipe(
      filter(([date, term]) => date !== null && term !== null 
        && date !== "" && term !== "")
      ).subscribe(([date, term]) => {
        this.calculateEndDate(date, term);
      });

    this.form.get("subscriptionDetails").valueChanges.subscribe(details => {
      this.updateUsageDetails(details);
      if(details.assetLimit && this.assetCount) {this.assetCountWarning = getUsageThreshold(details.assetLimit, this.assetCount);}
    })
  }

  calculateEndDate(date: Date, termId: string): void {
    const termInYears = (this.contractTerms.find(x => x.id === termId)?.name);

    if(termInYears){
      const endDate = new Date(date);
      endDate.setMonth(endDate.getMonth() + convert("years", "months", +termInYears));
      
      this.form.get("subscriptionEndDate").setValue(endDate);
    }
  }

  refreshCustomerTypes(){
    this.customerTypeRefresh$.next(null);
  }

  refreshCustomerInstanceUrls(){
    this.customerInstanceUrlRefresh$.next(null);
  }

  refreshContractTerms(){
    this.contractTermRefresh$.next(null);
  }

  createAddLookUpFunc(controllerName: string) : (name: string) => Observable<string> {
    return name => this.lookupService.addNew(controllerName, {name})
      .pipe(this.createHttpResponseMap<Lookup>())
  }

  createEditLookUpFunc(controllerName: string) : (item: Lookup) => Observable<string> {
    return item => this.lookupService.edit(controllerName, item)
      .pipe(this.createHttpResponseMap<Lookup>())
  }

  createDeleteLookUpFunc(controllerName: string) : (item: Lookup) => Observable<string> {
    return item => this.lookupService.delete(controllerName, item)
      .pipe(this.createHttpResponseMap<Lookup>())
  }

  createRefreshLookupObs(controllerName: string, subject: BehaviorSubject<void>) : Observable<PagedResults<Lookup>>{
    return subject.pipe(
        switchMap(() => this.lookupService.getList(controllerName, {})),
        shareReplay(1),
        takeUntil(this.unSubscribe$)
    )
  }

  createHttpResponseMap<T extends HasId>(): OperatorFunction<HttpResponse<T>, string> {
    return map((x: HttpResponse<T>) => {
          if(x.status === 200) {return x.body.id;}
          if(x.status === 204) {return "No Content";}
          else if(x instanceof HttpErrorResponse){
            const error = x as HttpErrorResponse;
            throw new Error(error.error.error.details[0].message);
          }
        })
  }

  updateUsageDetails(details: SubscriptionDetails){
      if(!this._usage) {return;}

      const { storageLimit, redactionLimit, concurrentUsersLimit, incidentCreationLimit, liveVideoLimit, xbaTranscodeLimit } = details;
      const storageLimitBytes = convert('terabytes', 'bytes', storageLimit);
      const redactionLimitSeconds = convert('minutes', 'seconds', redactionLimit);
      const nullSafeConvertBytes = nullCheckWrap((value: number) => convert("bytes", "terabytes", value, 1));
      const nullSafeRound = nullCheckWrap((value: number) => round(value, 0));
      const nullSafeConvertSecs = nullCheckWrap((value: number) => convert("seconds", "minutes", value, 0));

      this.usageDetails = {
        blobStorageUsed: {
          amount: nullSafeConvertBytes(this._usage.blobStorageUsed),
          warningLevel: getUsageThreshold(storageLimitBytes, this._usage.blobStorageUsed)
        },

        incidentsCreated: {
          amount: this._usage.incidentsCreated,
          warningLevel: getUsageThreshold(incidentCreationLimit, this._usage.incidentsCreated)
        },

        loggedInUsers: {
          amount: this._usage.loggedInUsers,
          warningLevel: getUsageThreshold(concurrentUsersLimit, this._usage.loggedInUsers)
        },

        minutesStreamed: {
          amount: nullSafeRound(this._usage.minutesStreamed),
          warningLevel: getUsageThreshold(liveVideoLimit, this._usage.minutesStreamed)
        },

        secondsRedacted: {
          amount: nullSafeConvertSecs(this._usage.secondsRedacted),
          warningLevel: getUsageThreshold(redactionLimitSeconds, this._usage.secondsRedacted)
        },

        // Already minutes both sides, so unlike redaction there is nothing to convert.
        xbaTranscodeMinutes: {
          amount: nullSafeRound(this._usage.xbaTranscodeMinutes),
          warningLevel: getUsageThreshold(xbaTranscodeLimit, this._usage.xbaTranscodeMinutes)
        }
      }
  }

  preventNonInteger(event){
    ["e", "E", "+", "-"].includes(event.key) && event.preventDefault()
  }
}

